/**
 * Determines whether a string contains possibly invalid or disallowed html.
 * @param {string} html String to validate. May or may not contain html.
 * @param {object} allowList Dictionary object containing allow-listed tags.
 * @returns True, if the html passes validation. False, otherwise.
 */
export function isValidHtml(html, allowList) {
    allowList = allowList || DefaultAllowList;
    // Build a document from the proposed html, creating
    // an object we can check against the allowlist.
    const domParser = new DOMParser();
    const document = domParser.parseFromString("<div>" + html + "</div>", "text/html");
    const elements = document.querySelectorAll("*");

    // the document parsed from the test string will always have <html><head><body>[...]
    // before the test content
    for (let i = 3; i < elements.length; i++) {
        const el = elements[i];
        // Check each tag against allowlist
        const isValidTag = allowList[el.nodeName.toLowerCase()];
        if (!isValidTag) {
            return false;
        } else if (el.attributes) {
            // Check attributes against allowlist
            for (const attr of el.attributes) {
                const validateFunc = DefaultAttributeAllowList[attr.name.toLocaleLowerCase()];

                if (attr.specified && (!validateFunc || !validateFunc(attr.nodeValue))) {
                    return false;
                }
            }
        }
    }

    return true;
}

const DefaultAllowList = {
    p: "p",
    strong: "strong",
    em: "em",
    b: "b",
    i: "i",
    font: "font",
    u: "u",
    a: "a",
    big: "big",
    small: "small",
    sub: "sub",
    sup: "sup",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    table: "table",
    thead: "thead",
    tbody: "tbody",
    tr: "tr",
    td: "td",
    th: "th",
    l: "l",
    li: "li",
    ul: "ul",
    ol: "ol",
    hr: "hr",
    br: "br",
    href: "href",
    name: "name",
    colspan: "colspan",
    rowspan: "rowspan",
    start: "start",
    style: "style",
    pre: "pre",
    s: "s",
    address: "address",
    caption: "caption",
    span: "span",
    div: "div",
    blockquote: "blockquote",
    "#text": "#text"
};
const DefaultAttributeAllowList = {
    src: isValidUrl,
    href: isValidUrl,
    border: isValidDistance,
    width: isValidDistance,
    height: isValidDistance,
    cellspacing: isValidDistance,
    cellpadding: isValidDistance,
    target: isValidTarget,
    name: isValidPassthrough,
    colspan: isValidNumber,
    rowspan: isValidNumber,
    start: isValidNumber,
    style: isValidStyle,
    "data-resource-id": isValidPassthrough,
    "data-resource-type": isValidResourceType
};
const DefaultStyleAttributeAllowList = {
    display: isValidDisplay,
    padding: isValidAggregatePadding,
    "padding-left": isValidPadding,
    "padding-right": isValidPadding,
    "padding-top": isValidPadding,
    "padding-bottom": isValidPadding,
    margin: isValidAggregateMargin,
    "margin-left": isValidMargin,
    "margin-right": isValidMargin,
    "margin-top": isValidMargin,
    "margin-bottom": isValidMargin,
    width: isValidMargin,
    height: isValidMargin,
    "font-family": isValidFont,
    "font-size": isValidFontSize,
    "font-weight": isValidFontWeight,
    color: isValidColor,
    "background-color": isValidColor,
    "list-style-type": isValidListStyle,
    "text-align": isValidTextAlign,
    "text-decoration": isValidTextDecoration
};
const DefaultFontFamilyAllowList = [
    "andale mono",
    "arial",
    "arial black",
    "avant garde",
    "book antiqua",
    "chicago",
    "comic sans ms",
    "courier",
    "courier new",
    "geneva",
    "georgia",
    "helvetica",
    "impact",
    "monaco",
    "palatino",
    "sans-serif",
    "serif",
    "symbol",
    "tahoma",
    "terminal",
    "times",
    "times new roman",
    "trebuchet ms",
    "verdana",
    "webdings",
    "wingdings",
    "zapf dingbats"
];
const SpaceUnits = ["%", "cm", "em", "ex", "in", "mm", "pc", "pt", "px"];
const DefaultColors = [
    "aqua",
    "black",
    "blue",
    "fuchsia",
    "gray",
    "green",
    "lime",
    "maroon",
    "navy",
    "olive",
    "orange",
    "purple",
    "red",
    "silver",
    "teal",
    "white",
    "yellow"
];

function isValidUrl(value) {
    let url;
    try {
        url = new URL(value);
    } catch (_) {
        return false;
    }

    return url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:";
}

function isValidDistance(attributeVal) {
    if (!isNaN(attributeVal)) {
        return true;
    }
    let isValid = false;
    for (const unit of SpaceUnits) {
        if (
            attributeVal.endsWith(unit) &&
            attributeVal != unit &&
            !isNaN(attributeVal.substring(0, attributeVal.length - unit.length))
        ) {
            isValid = true;
            break;
        }
    }
    return isValid;
}

function isValidTarget(attributeVal) {
    return attributeVal === "" || attributeVal === "_blank";
}

function isValidNumber(attributeVal) {
    return !isNaN(attributeVal);
}

function isValidPassthrough() {
    return true; // validateResource and validateName were passthroughs in legacy
}

function isValidResourceType(attributeVal) {
    return attributeVal === "dashboard" || attributeVal === "form";
}

function isValidDisplay(attributeVal) {
    return attributeVal === "inline" || attributeVal === "block";
}

function isValidStyle(attributeVal) {
    const attributes = attributeVal.split(";");
    let isValid = true;

    // Verify each style attribute separately
    for (const attribute of attributes) {
        // Skip empty styles
        if (attribute === "") {
            continue;
        }

        const attributeTuple = attribute.split(":");

        // Each style attribute must have a key and value
        if (attributeTuple.length < 2 || !attributeTuple[0] || !attributeTuple[1]) {
            isValid = false;
            break;
        }

        const styleName = attributeTuple[0].trim();
        const styleVal = attributeTuple[1].trim();
        const isValidFunction = DefaultStyleAttributeAllowList[styleName];
        if (!isValidFunction || !isValidFunction(styleVal)) {
            isValid = false;
            break;
        }
    }
    return isValid;
}

function isValidSpacing(attributeVal, validAttributes) {
    if (validAttributes.includes(attributeVal)) {
        return true;
    }
    return isValidDistance(attributeVal);
}

function isValidPadding(attributeVal) {
    return isValidSpacing(attributeVal, ["inherit", "initial"]);
}

function isValidMargin(attributeVal) {
    return isValidSpacing(attributeVal, ["auto", "inherit", "initial"]);
}

function isValidAggregateSpace(attributeVal, validAttributes) {
    if (attributeVal == null) {
        return false;
    }
    const values = attributeVal.split(" ");
    let isValid = true;
    for (const val of values) {
        if (!isValidSpacing(val.trim(), validAttributes)) {
            isValid = false;
            break;
        }
    }
    return isValid;
}

function isValidAggregateMargin(attributeVal) {
    return isValidAggregateSpace(attributeVal, ["auto", "inherit", "initial"]);
}

function isValidAggregatePadding(attributeVal) {
    return isValidAggregateSpace(attributeVal, ["inherit", "initial"]);
}

function isValidFont(attributeVal) {
    if (attributeVal == null) {
        return false;
    }
    if (attributeVal === "inherit" || attributeVal === "initial") {
        return true;
    }

    const attributes = attributeVal.split(",");
    let isValid = true;
    for (const attribute of attributes) {
        if (!DefaultFontFamilyAllowList.includes(attribute.trim())) {
            isValid = false;
            break;
        }
    }

    return isValid;
}

function isValidFontSize(attributeVal) {
    return isValidSpacing(attributeVal, [
        "medium",
        "xx-small",
        "x-small",
        "small",
        "medium",
        "large",
        "x-large",
        "xx-large",
        "smaller",
        "larger",
        "initial",
        "inherit"
    ]);
}

function isValidFontWeight(attributeVal) {
    return isValidSpacing(attributeVal, ["normal", "bold", "bolder", "lighter", "initial", "inherit"]);
}

function isValidListStyle(attributeVal) {
    return [
        "lower-alpha",
        "upper-alpha",
        "lower-greek",
        "lower-roman",
        "upper-roman",
        "square",
        "disc",
        "circle"
    ].includes(attributeVal);
}

function isValidTextAlign(attributeVal) {
    return ["justify", "center", "left", "right"].includes(attributeVal);
}

function isValidTextDecoration(attributeVal) {
    return ["line-through", "underline"].includes(attributeVal);
}

function isValidColor(attributeVal) {
    if (DefaultColors.includes(attributeVal)) {
        return true;
    }

    const regex =
        attributeVal.length > 7
            ? // eslint-disable-next-line max-len
              /rgb\(([0-9]{1,2}|1[0-9]{2}|2[0-4][0-9]|25[0-5]),\s([0-9]{1,2}|1[0-9]{2}|2[0-4][0-9]|25[0-5]),\s([0-9]{1,2}|1[0-9]{2}|2[0-4][0-9]|25[0-5])\)/
            : /#[A-Fa-f0-9]{6}/;

    return attributeVal.match(regex) != null;
}
